/**
 * Perfil — v1.8 (Feb 2026).
 *
 * User-facing "Mi cuenta" hub. Uses the reference image layout:
 *   - Header with back arrow + "MI CUENTA" + credits chip + avatar
 *   - Large circular avatar with camera icon (uploads via /auth/avatar)
 *   - Name + email + plan pill
 *   - 5 menu items (Mi cuenta, Créditos, Preferencias, Seguridad, Soporte)
 *   - Cerrar sesión red button
 *
 * Every item routes to a real dedicated screen — no dead links, no
 * "coming soon".
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, ChevronRight, User, Coins, Sliders, ShieldCheck, LifeBuoy, LogOut, Languages, Volume2, Shield } from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import { useT } from '../contexts/LocaleContext';
import { toast } from 'sonner';
import { CreditsChip } from '../components/user/UserTopBar';
import { AdminAvatarMenu } from '../components/shell/AdminAvatarMenu';
import { AvatarCropperModal } from '../components/user/AvatarCropperModal';

export default function Perfil() {
  const nav = useNavigate();
  const auth = useAuth();
  const isAdmin = auth.user?.role === 'admin';
  const { balance } = useCredits();
  const t = useT();
  const [me, setMe] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [cropperSrc, setCropperSrc] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    try { setMe(await apiService.getMe()); }
    catch { setMe(null); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPickAvatar = () => fileRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error(t('profile.photoTooLarge'));
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    // Load the raw file into a data URL and open the crop editor.
    // The user then decides zoom / pan / center before we upload.
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setCropperSrc(reader.result);
    };
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onCropperSave = async (croppedJpegDataUrl: string) => {
    setUploading(true);
    try {
      const r = await apiService.uploadAvatar(croppedJpegDataUrl);
      const busted = r.avatar_url + (r.avatar_url.includes('?') ? '&' : '?') + 'v=' + Date.now();
      // Sprint P0.5 v1.48 · Refetch /me para que TODOS los consumidores
      // de AuthContext (avatar del topbar, /soporte del admin, etc.)
      // reciban el nuevo `updated_at` y la URL final del servidor. Sin
      // este refetch la cache-buster funcionaba solo dentro de /perfil
      // pero el AdminAvatarMenu del topbar podía seguir con la imagen
      // vieja según el momento en que se leyera el user.
      setMe((prev: any) => ({ ...(prev || {}), avatar_url: busted }));
      if (auth.user) auth.updateUser({ ...auth.user, avatar_url: busted } as any);
      try {
        const fresh: any = await apiService.getMe();
        if (fresh) {
          setMe((prev: any) => ({ ...(prev || {}), ...fresh }));
          if (auth.updateUser) auth.updateUser(fresh);
        }
      } catch { /* non-fatal */ }
      toast.success(t('profile.photoUpdated'));
      setCropperSrc(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'No se pudo subir la foto.');
    } finally {
      setUploading(false);
    }
  };

  const logout = () => {
    auth.logout();
    nav('/auth', { replace: true });
  };

  const plan = (me?.role === 'admin' || me?.role === 'owner') ? 'PRO PLAN' : 'FREE PLAN';

  return (
    <div className="min-h-screen bg-slate-950 nx-circuit text-slate-100" data-testid="perfil-page">
      <header
        className="sticky top-0 z-20 backdrop-blur-md"
        style={{
          background: 'linear-gradient(180deg, hsl(var(--bg-elev) / 0.85), hsl(var(--bg-elev) / 0.4))',
          borderBottom: '1px solid hsl(var(--border))',
        }}
      >
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => nav('/home')}
            className="inline-flex items-center gap-1.5 text-sm"
            style={{ color: 'hsl(var(--text-muted))' }}
            data-testid="perfil-back"
          >
            <ArrowLeft size={16} /> {t('common.back')}
          </button>
          <div className="text-xs tracking-[0.24em] uppercase font-semibold" style={{ color: 'hsl(var(--accent))' }}>
            {t('menu.myAccount')}
          </div>
          <div className="flex items-center gap-2">
            <CreditsChip balance={balance} onClick={() => nav('/creditos')} />
            <div data-testid="topbar-avatar"><AdminAvatarMenu /></div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Avatar + identity */}
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <button
              onClick={onPickAvatar}
              disabled={uploading}
              className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden grid place-items-center relative"
              style={{
                background: 'hsl(var(--bg-elev))',
                border: '2px solid hsl(var(--accent) / 0.55)',
                boxShadow: '0 0 32px hsl(var(--accent) / 0.25)',
              }}
              data-testid="perfil-avatar-btn"
              aria-label="Cambiar foto de perfil"
            >
              {me?.avatar_url ? (
                <img
                  src={me.avatar_url.startsWith('http') ? me.avatar_url : ((process.env.REACT_APP_BACKEND_URL || '') + me.avatar_url)}
                  alt="avatar"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // If the image 404s (rare — cleared uploads dir), silently
                    // fall back to the placeholder icon so we never render a
                    // broken image glyph.
                    const el = e.currentTarget as HTMLImageElement;
                    el.style.display = 'none';
                    setMe((prev: any) => ({ ...(prev || {}), avatar_url: null }));
                  }}
                />
              ) : (
                <User size={44} style={{ color: 'hsl(var(--text-muted))' }} />
              )}
              <div
                className="absolute bottom-1 right-1 w-8 h-8 rounded-full grid place-items-center"
                style={{
                  background: 'hsl(var(--accent))',
                  color: 'hsl(210 40% 8%)',
                  border: '3px solid hsl(var(--bg))',
                }}
              >
                <Camera size={14} />
              </div>
            </button>
            <input
              ref={fileRef} type="file" accept="image/*"
              className="hidden" onChange={onFileChange}
              data-testid="perfil-avatar-input"
            />
          </div>
          {/* Sprint UX Workspace v1.34 · modern circular cropper */}
          {cropperSrc && (
            <AvatarCropperModal
              fileDataUrl={cropperSrc}
              onCancel={() => setCropperSrc(null)}
              onSave={onCropperSave}
              saving={uploading}
            />
          )}

          <h1 className="mt-4 text-xl sm:text-2xl font-semibold" data-testid="perfil-name">
            {me?.name || auth.user?.name || 'Usuario'}
          </h1>
          <div className="text-sm mt-0.5" style={{ color: 'hsl(var(--text-muted))' }} data-testid="perfil-email">
            {me?.email || auth.user?.email || ''}
          </div>
          {/* Sprint v1.29 · #12 · empresa + bio visibles en la ficha */}
          {me?.company && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs"
                 style={{ color: 'hsl(var(--text))' }}
                 data-testid="perfil-company">
              <span className="opacity-60">·</span>
              <span>{me.company}</span>
            </div>
          )}
          {me?.bio && (
            <p className="mt-3 max-w-md mx-auto text-sm leading-relaxed"
               style={{ color: 'hsl(var(--text-muted))' }}
               data-testid="perfil-bio">
              {me.bio}
            </p>
          )}
          <span
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold"
            style={{
              background: 'hsl(var(--accent) / 0.14)',
              border: '1px solid hsl(var(--accent) / 0.35)',
              color: 'hsl(var(--accent))',
            }}
            data-testid="perfil-plan-badge"
          >
            {plan}
          </span>
        </div>

        {/* Menu — Sprint v1.16 · point #3 unified panel: Mi cuenta /
            Créditos / Idioma / Voz y Audio / Soporte / Cerrar sesión.
            Preferencias antiguas y Seguridad viven ahora dentro de
            "Mi cuenta" para eliminar navegaciones duplicadas. */}
        <div className="mt-8 space-y-3">
          <MenuItem
            testId="perfil-menu-cuenta"
            icon={<User size={18} />}
            title={t('profile.menuAccountTitle')}
            subtitle={t('profile.menuAccountSubtitle')}
            onClick={() => nav('/perfil/cuenta')}
          />
          <MenuItem
            testId="perfil-menu-creditos"
            icon={<Coins size={18} />}
            title={t('profile.menuCreditsTitle')}
            subtitle={t('profile.menuCreditsSubtitle')}
            right={<span className="text-xs font-mono" style={{ color: 'hsl(var(--accent))' }}>{balance ?? '—'} {t('profile.menuCreditsAvailable')}</span>}
            onClick={() => nav('/creditos')}
          />
          <MenuItem
            testId="perfil-menu-idioma"
            icon={<Languages size={18} />}
            title={t('profile.menuLanguageTitle')}
            subtitle={t('profile.menuLanguageSubtitle')}
            onClick={() => nav('/perfil/idioma')}
          />
          <MenuItem
            testId="perfil-menu-audio"
            icon={<Volume2 size={18} />}
            title={t('profile.menuAudioTitle')}
            subtitle={t('profile.menuAudioSubtitle')}
            onClick={() => nav('/perfil/audio')}
          />
          <MenuItem
            testId="perfil-menu-soporte"
            icon={<LifeBuoy size={18} />}
            title={t('profile.menuSupportTitle')}
            subtitle={t('profile.menuSupportSubtitle')}
            onClick={() => nav('/soporte')}
          />
          {isAdmin && (
            <MenuItem
              testId="perfil-menu-admin"
              icon={<Shield size={18} />}
              title={t('profile.menuAdminTitle')}
              subtitle={t('profile.menuAdminSubtitle')}
              onClick={() => nav('/admin')}
              accent
            />
          )}
        </div>

        {/* Sprint Arquitectura Definitiva #3 · Switch Producción/Desarrollo
            (solo visible para administradores). No cambia el dominio ni la
            UI: cambia el backend al que apunta la sesión actual y persiste
            en localStorage['nexus.backend'] para el próximo boot. */}
        {isAdmin && (
          <div
            className="w-full mt-6 rounded-2xl p-4"
            style={{ background: 'hsl(var(--bg-elev) / 0.55)', border: '1px solid hsl(var(--border))' }}
            data-testid="perfil-backend-switch"
          >
            <div className="text-[10px] uppercase tracking-widest font-semibold mb-2"
                 style={{ color: 'hsl(var(--accent))' }}>
              {t('profile.backendEnvironment')}
            </div>
            <BackendSwitch />
          </div>
        )}

        <button
          onClick={logout}
          className="w-full mt-6 rounded-2xl px-5 py-4 flex items-center justify-center gap-2 text-sm font-medium"
          style={{
            background: 'hsl(0 70% 40% / 0.14)',
            border: '1px solid hsl(0 70% 55% / 0.4)',
            color: 'hsl(0 70% 65%)',
          }}
          data-testid="perfil-logout-btn"
        >
          <LogOut size={16} /> {t('profile.signOut')}
        </button>
      </main>
    </div>
  );
}

const MenuItem: React.FC<{
  testId: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  right?: React.ReactNode;
  onClick: () => void;
  accent?: boolean;
}> = ({ testId, icon, title, subtitle, right, onClick, accent }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="w-full text-left rounded-2xl px-4 py-3 flex items-center gap-3 transition group"
    style={{
      background: accent ? 'hsl(var(--accent) / 0.08)' : 'hsl(var(--bg-elev) / 0.7)',
      border: `1px solid ${accent ? 'hsl(var(--accent) / 0.4)' : 'hsl(var(--border))'}`,
      backdropFilter: 'blur(10px)',
    }}
    onMouseEnter={(e) => {
      (e.currentTarget as HTMLButtonElement).style.borderColor = 'hsl(var(--accent) / 0.5)';
      (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
    }}
    onMouseLeave={(e) => {
      (e.currentTarget as HTMLButtonElement).style.borderColor = accent ? 'hsl(var(--accent) / 0.4)' : 'hsl(var(--border))';
      (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
    }}
  >
    <div
      className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
      style={{
        background: 'hsl(var(--accent) / 0.14)',
        border: '1px solid hsl(var(--accent) / 0.32)',
        color: 'hsl(var(--accent))',
      }}
    >
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium truncate">{title}</div>
      <div className="text-xs mt-0.5 line-clamp-2" style={{ color: 'hsl(var(--text-muted))' }}>{subtitle}</div>
    </div>
    {right ? <div className="mr-1 shrink-0">{right}</div> : null}
    <ChevronRight size={16} style={{ color: 'hsl(var(--text-muted))' }} />
  </button>
);


// ── Sprint Arquitectura Definitiva #3 · Backend switch ─────────────
// Solo el usuario admin puede cambiar entre backend Producción y
// backend Desarrollo. El switch persiste la elección en localStorage
// y hace un soft-reload para que el axios interceptor use la nueva
// URL. No cambia dominio ni interfaz.
const BackendSwitch: React.FC = () => {
  const t = useT();
  const current: 'prod' | 'dev' =
    (typeof window !== 'undefined' && window.localStorage.getItem('nexus.backend') === 'dev')
      ? 'dev' : 'prod';
  const apply = (target: 'prod' | 'dev') => {
    try {
      window.localStorage.setItem('nexus.backend', target);
      toast.success(
        (target === 'dev' ? t('profile.backendDev') : t('profile.backendProd'))
        + ' · ' + t('profile.backendActive'),
      );
      // Soft reload so services/api.ts picks up the new base URL.
      window.location.reload();
    } catch {
      toast.error(t('profile.backendChangeFailed'));
    }
  };
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="backend-switch-grid">
      <button
        onClick={() => apply('prod')}
        data-testid="backend-switch-prod"
        aria-pressed={current === 'prod'}
        className="rounded-xl px-3 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition"
        style={{
          background: current === 'prod' ? 'hsl(150 65% 45% / 0.2)' : 'hsl(var(--bg) / 0.6)',
          border: `1px solid ${current === 'prod' ? 'hsl(150 65% 45% / 0.6)' : 'hsl(var(--border))'}`,
          color: current === 'prod' ? 'hsl(150 65% 65%)' : 'hsl(var(--text-muted))',
        }}
      >
        <span className="w-2 h-2 rounded-full" style={{ background: 'hsl(150 65% 55%)' }} />
        {t('profile.backendProd')} {current === 'prod' && <span className="text-[10px] ml-1 opacity-80">{t('profile.backendActive')}</span>}
      </button>
      <button
        onClick={() => apply('dev')}
        data-testid="backend-switch-dev"
        className="rounded-xl px-3 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition"
        style={{
          background: current === 'dev' ? 'hsl(38 92% 55% / 0.2)' : 'hsl(var(--bg) / 0.6)',
          border: `1px solid ${current === 'dev' ? 'hsl(38 92% 55% / 0.6)' : 'hsl(var(--border))'}`,
          color: current === 'dev' ? 'hsl(38 92% 65%)' : 'hsl(var(--text-muted))',
        }}
      >
        <span className="w-2 h-2 rounded-full" style={{ background: 'hsl(38 92% 55%)' }} />
        {t('profile.backendDev')} {current === 'dev' && <span className="text-[10px] ml-1 opacity-80">{t('profile.backendActive')}</span>}
      </button>
    </div>
  );
};

// Sprint v1.29 · #13 · Compresión inteligente de imagen para avatar.
// Redimensiona a un cuadrado centrado y comprime a JPEG. Acepta HEIC,
// PNG, JPEG, WEBP. Fallback: si Canvas falla, devuelve el data URL
// original.
async function smartCompressImage(
  file: File,
  { maxSide = 800, quality = 0.85 }: { maxSide?: number; quality?: number } = {},
): Promise<string> {
  const readAsDataUrl = (f: File) =>
    new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result as string);
      r.onerror = () => rej(r.error);
      r.readAsDataURL(f);
    });
  const original = await readAsDataUrl(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const el = new Image();
      el.onload = () => res(el);
      el.onerror = () => rej(new Error('image load failed'));
      el.src = original;
    });
    const side = Math.min(img.width, img.height);
    const sx = (img.width - side) / 2;
    const sy = (img.height - side) / 2;
    const targetSide = Math.min(maxSide, side);
    const canvas = document.createElement('canvas');
    canvas.width = targetSide;
    canvas.height = targetSide;
    const ctx = canvas.getContext('2d');
    if (!ctx) return original;
    ctx.imageSmoothingEnabled = true;
    (ctx as any).imageSmoothingQuality = 'high';
    ctx.drawImage(img, sx, sy, side, side, 0, 0, targetSide, targetSide);
    let out = canvas.toDataURL('image/jpeg', quality);
    // If still too large, iterate quality down.
    let q = quality;
    while (out.length > 350_000 && q > 0.5) {
      q -= 0.1;
      out = canvas.toDataURL('image/jpeg', q);
    }
    return out;
  } catch {
    return original;
  }
}
